import { I18n } from 'react-redux-i18n';
import { Flex } from '@common/components/Flex';
import cx from 'classnames';
import isNil from 'lodash/isNil';
import mapValues from 'lodash/mapValues';

import { getFilters } from '@common/actions/Filters';
import { MessageBox } from '@common/components/MessageBox';
import { ErrorIcon } from '@common/components/error-icon';
import { InfoIcon } from '@common/components/info-icon';
import { getTestAttributes } from '@common/utils/tests';
import { Tabs } from '@common/components/tabs';
import { MoveFolder } from '@common/components/move-folder';
import { NewTableSortingDirection } from '@common/components/new-table/new-table.types';
import { FolderManagement, FolderManagementActionMode } from '@common/components/folder-management';
import { Folders } from '@common/components/folders';
import { TreeActionType } from '@common/components/Tree';
import { getFolderSecuritySettingsClasses } from "@common/actions/user-maintenance/classes";
import { savePedigreeFolder, getPedigreeFolder, movePedigreeFolder, deletePedigreeFolder, renamePedigreeFolder, getFolders } from '@common/actions/folders';
import { getFolderFormats, getFormatFolders, createFormatFolder, deleteFormatFolder, deleteFormat } from '@common/actions/formats';
import { IndividualsTable } from './components/individuals-table';
import { InvitesTable } from './components/invites-table';
import { Header } from './components/header';
import { changeIndividualGender, callAuditIndividualView } from './actions/individuals';
import {
  applyCurrentColumnsToAllUsers,
  applyCurrentColumnsToSpecificProfiles,
  getIndividualsColumns,
  restoreIndividualsColumnsToDefault,
  saveIndividualsColumns,
  setDefaultColumnsForSpecificUsers,
  setDefaultColumnsForToAllUsers,
  openNewSpreadsheet,
  showProbandsOnly,
  checkIndividualNameExists,
  deleteIndividual
} from './actions/individuals';
import { getInvites } from './actions/invites';
import { NewIndividual } from "./components/new-individual";
import { CONTEXT_TYPE } from './constants/context';
import { scrollTopLeft } from '@common/components/new-table/new-table.utils';
import { generateId } from '@common/utils/common';
import { getFamilyIdMembersCount, getIndividualFamilyData, getSentQuestionnaires, openDatasheetFromIndividual, openPedigreeFromIndividual, renameIndividual, updateLockStatus, getIndividualEmail, saveIndividualEmail, sendInvite, getInviteHistory, getIndividuals } from '@common/actions/individuals';
import { getProfiles } from '@common/actions/user-maintenance/profiles';
import { getAdminSmartListFunctions, getUserPermissions } from '@common/actions/permissions';
import { getInvitesColumns, saveInvitesColumns, restoreInvitesColumnsToDefault } from '@common/actions/invites';
import { QuestionnaireOpen } from '@common/components/questionnaire-open';
import { getQuestionnaireForInvite, getQuestionnaires, openInviteQuestionnaire } from '@common/actions/questionnaires';
import { getCurrentUrl } from '@common/utils/url';
import { checkPedigreeNameExists, createPedigreeAndMoveToFolder, getPedigreeFolders } from '@common/actions/pedigrees';
import { NEW_INVITE_STEPS, NewInvite } from '@common/components/new-invite';

import './individuals.styles.scss';
import React from "react";

export const MODULE_NAME = 'Individuals';

export class Individuals extends React.Component {
  individualDetailsTabsTypes = {
    invites: 'INVITES',
  };
  customIndividualUniqProperty = 'customIndividualUniqPropertyForUI';
  customInviteUniqProperty = 'customInviteUniqPropertyForUI';
  parentIndividualIdOfCurrentInvites = null;
  individualsTableRef = React.createRef();
  state = {
    selectedFolderId: null,
    probandsOnly: false,
    loadingIndividuals: true,
    search: '',
    folders: [],
    messageProps: {
      header: '',
      text: '',
      show: false,
      confirmText: 'Ok',
      cancelText: '',
      size: 'medium',
      onCustomOk: () => {},
    },

    tableFilters: {},
    loginToken: null,
    individuals: [],
    individualsTotalCount: 0,
    selectedIndividuals: [],
    selectedIndividualDetailsTab: this.individualDetailsTabsTypes.invites,
    individualDetailsOpened: true,
    individualsTableColumns: [],
    individualsTableLoadMetadata: {
      pagination: {
        start: 0,
        size: 45,
      },
    },
    
    selectedInvites: [],
    invites: [],
    invitesTotalCount: 0,
    invitesTableColumns: [],
    invitesTableLoadMetadata: {
      pagination: {
        start: 0,
        size: 45,
      },
    },
    newInviteDialogProps: {
      individualId: null,
      opened: false,
    },
    openQuestionnaireDialogProps: {
      id: null,
      opened: false,
      itemName: '',
    },

    sendReminderDialogProps: {
      fhq4: false,
      inviteId: null,
      individualId: null,
      opened: false,
    },
    newIndividualDialogProps: {
      opened: false,
    },
    folderManagementDialogProps: {
      opened: false,
    },
    moveFolderDialogProps: {
      opened: false,
    },
    userPermissions: {
      admin: false,
      canAddFolder: false,
      canModifyFolder: false,
      canDeleteFolder: false,
    }
  };

  componentDidMount() {
    this.initialize();
  }

  initialize = async () => {
    try {
      this.state.userPermissions = await getUserPermissions();
      this.getFilters();
      this.getInvitesTableColumns();
      
      const { defaultFolderId } = await this.getFolders();
      const probandsOnly = await this.showProbandsOnly();
      const individualsColumns = await this.getIndividualsTableColumns();
      await this.getIndividuals({
        columns: individualsColumns,
        selectedFolderId: defaultFolderId,
        probandsOnly,
        individuals: [],
        filtering: this.state.individualsTableLoadMetadata.filtering,
        pagination: this.state.individualsTableLoadMetadata.pagination,
        sorting: this.state.individualsTableLoadMetadata.sorting,
      });
    } catch (err) {
      const errorMessage = err?.entity || I18n.t('Failed to get data');

      this.showErrorMessage(errorMessage);
      console.error(err);
    }
  }

  getFolders = async () => {
    try {
      const individualsContext = 1;
      const folders = await getFolders({ context: individualsContext });
      this.setState((x) => ({ ...x, folders }));
      const defaultFolderId = this.getDefaultFolderId(folders);

      if (isNil(this.state.selectedFolderId) && defaultFolderId) {
        this.setSelectedFolderId(defaultFolderId, false);
      }

      return { folders, defaultFolderId };
      
    } catch (err) {
      console.error(err);

      const errorMessage = err?.entity || I18n.t('Failed to get folders');
      this.showErrorMessage(errorMessage);
    }
  }

  getFolder = async (folderId) => {
    try {
      const folder = await getPedigreeFolder(folderId);
      
      return folder;
    } catch (err) {
      console.error(err);

      const errorMessage = err?.entity || I18n.t('Failed to get selected folder');
      this.showErrorMessage(errorMessage);
    }
  }
  
  getFolderSecuritySettingsClasses = async () => {
    try {
      const folderSecuritySettings = await getFolderSecuritySettingsClasses();
      
      return folderSecuritySettings;
    } catch (err) {
      console.error(err);

      const errorMessage = err?.entity || I18n.t('Failed to get Folder Security Settings');
      this.showErrorMessage(errorMessage);
      
    }
  }

  getFilters = () => {
    getFilters().then((tableFilters) => {
      const filtersProcessed = mapValues(tableFilters, (filterName) => {
        let isValidValue = undefined;

        if (filterName === 'is blank' || filterName === 'is not blank') {
          isValidValue = (value) => ['', null, undefined].includes(value);
        }

        return {
          name: filterName,
          isValidValue,
        };
      });

      this.setState((x) => ({
        ...x,
        tableFilters: filtersProcessed,
      }));
    });
  }

  showProbandsOnly = async () => {
    try {
      const probandsOnly = await showProbandsOnly();
      this.setState((x) => ({
        ...x,
        probandsOnly: probandsOnly,
      }));

      return probandsOnly;
    } catch (err) {
      const errorMessage = err?.entity || I18n.t('Failed to get the value of the "Probands" dropdown');

      this.showErrorMessage(errorMessage);
      console.error(err);

      return false;
    }
  }

  getIndividualsTableColumns = async () => {
    try {
      const columns = await getIndividualsColumns();

      this.setState((x) => ({ ...x, individualsTableColumns: columns }));

      return columns;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  getColumnId = (column) => {
    return `field_id__${column.field.id.toString()}__field_type__${column.field.type.toString()}__context__${column.field.context.toString()}`;
  }

  getIndividuals = async (params) => {
    const { filtering, pagination, sorting, probandsOnly, search } = params;
    const columns = params.columns.map((x, i) => {
      const notSortableActionColumn = 1;
      const isSorted = i === sorting?.index - notSortableActionColumn;

      return {
        ...x,
        sortOrder: isSorted ? 1 : 0,
        sortAsc: isSorted ? sorting?.direction === NewTableSortingDirection.asc : false,
      };
    });

    const filters = filtering?.columns || {};
    const columnFiltersProcessed = Object.keys(filters).map((columnId) => {
      const columnIndex = params.columns.findIndex((x) => this.getColumnId(x) == columnId);
      const columnToRemoveBecauseOfActionsColumn = 1;
      const value = filters[columnId];

      return {
        columnId: columnIndex + columnToRemoveBecauseOfActionsColumn,
        operator1: value.operator1 ?? null,
        operator2: value.operator2 ?? null,
        value1: value.whereClause1 ?? null,
        value2: value.whereClause2 ?? null,
        andOr: value.andOR,
      };
    });

    try {
      const { data, totalCount, loginToken } = await getIndividuals({
        pagination,
        filtering: {
          search: search ?? null,
          probandsOnly,
          filterApplied: filtering?.filter,
          columnFilters: columnFiltersProcessed,
          columns,
          folderId: params.selectedFolderId,
        },
      });

      const processedData = data.map((x) => {
        x[this.customIndividualUniqProperty] = generateId();

        return x;
      });

      const finalData = pagination.start > 0
        ? [...params.individuals, ...processedData]
        : processedData;

      if (pagination.start === 0) {
        scrollTopLeft(this.individualsTableRef.current);
      }

      this.setState((x) => ({
        ...x, 
        loginToken,
        individualsTableLoadMetadata: {
          filtering,
          pagination,
          sorting,
        },
        individualsTableColumns: columns,
        individuals: finalData,
        individualsTotalCount: pagination.start > 0 ? x.individualsTotalCount : totalCount,
      }));
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      this.setState((x) => ({ ...x, loadingIndividuals: false }));
    }
  }

  getInvites = async (params) => {
    try {
      const { filtering, pagination, sorting } = params;
  
      const columns = params.columns.map((x, i) => {
        const notSortableActionColumn = 1;
        const isSorted = i === sorting?.index - notSortableActionColumn;
  
        return {
          ...x,
          sortOrder: isSorted ? 1 : 0,
          sortAsc: isSorted ? sorting?.direction === NewTableSortingDirection.asc : undefined,
        };
      });
      const individualId = params.selectedIndividuals.length ? params.selectedIndividuals[0].id : null;
      const filters = filtering?.columns || {};
      const columnFiltersProcessed = Object.keys(filters).map((columnId) => {
        const columnIndex = params.columns.findIndex((x) => this.getColumnId(x) == columnId);
        const columnToRemoveBecauseOfActionsColumn = 1;
        const value = filters[columnId];

        return {
          columnId: columnIndex + columnToRemoveBecauseOfActionsColumn,
          operator1: value.operator1 ?? null,
          operator2: value.operator2 ?? null,
          value1: value.whereClause1 ?? null,
          value2: value.whereClause2 ?? null,
          andOr: value.andOR,
        };
      });
      const { data, totalCount } = await getInvites({
        pagination,
        filtering: {
          filterApplied: filtering?.filter,
          columnFilters: columnFiltersProcessed,
          columns,
          globalId: individualId
        },
      });

      this.parentIndividualIdOfCurrentInvites = individualId;
      
      const processedData = data.map((x) => {
        x[this.customInviteUniqProperty] = generateId();

        return x;
      });

      const finalData = pagination.start > 0
        ? [...params.invites, ...processedData]
        : processedData;
  
      this.setState((x) => ({
        ...x,
        invitesTableLoadMetadata: {
          filtering,
          pagination,
          sorting,
        },
        invitesTableColumns: columns,
        invites: finalData,
        invitesTotalCount: x.invitesTotalCount || totalCount,
      }));
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  getInvitesTableColumns = async () => {
    try {
      const columns = await getInvitesColumns();
  
      this.setState((x) => ({ ...x, invitesTableColumns: columns }));

      return columns;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  getDefaultFolderId(folders = this.state.folders) {
    const firstFolder = folders[0];

    return firstFolder?.id;
  }

  setSelectedFolderId = (id, refreshList = true, callback) => {
    if (this.state.selectedFolderId == id) {
      return;
    }

    this.setState(
      (state) => ({
        ...state,
        loadingIndividuals: true,
        individualsTableLoadMetadata: {
          ...state.individualsTableLoadMetadata,
          pagination: {
            start: 0,
            size: 45,
          },
        },
        selectedFolderId: id,
        selectedIndividuals: [],
        selectedInvites: [],
        individualsTotalCount: 0,
        invites: [],
        invitesTotalCount: 0,
      }), 
      refreshList 
        ? () => this.getIndividuals({
            search: this.state.search,
            columns: this.state.individualsTableColumns,
            selectedFolderId: id,
            probandsOnly: this.state.probandsOnly,
            individuals: [],
            filtering: this.state.individualsTableLoadMetadata.filtering,
            pagination: {
              start: 0,
              size: 45,
            },
            sorting: this.state.individualsTableLoadMetadata.sorting,
          })
        : callback
    );
  }

  handleSelectedIndividualChange = async (items) => {
    const sameSelected = 
      this.state.selectedIndividuals.length === 1 &&
      items.length === 1 &&
      this.state.selectedIndividuals[0][this.customIndividualUniqProperty] === items[0][this.customIndividualUniqProperty];

    if (sameSelected) {
      return;
    }

    const finalInvites = items.length > 1 ? this.state.invites : [];
    const finalInvitesLoadMetadata = items.length > 1
      ? this.state.invitesTableLoadMetadata
      : {
        ...this.state.invitesTableLoadMetadata,
        pagination: {
          start: 0,
          size: 45,
        },
      };

    this.setState(
      (state) => ({
        ...state,
        invitesTableLoadMetadata: finalInvitesLoadMetadata,
        invites: finalInvites,
        selectedIndividuals: items,
        selectedInvites: [],
      }), 
    );

    if (items.length === 1) {
      const invitesColumns = await this.getInvitesTableColumns();
      await this.getInvites({
        columns: invitesColumns,
        invites: finalInvites,
        selectedIndividuals: items,
        ...finalInvitesLoadMetadata,
      });

      callAuditIndividualView(items[0].id)
    }
  }

  handleSelectedInvitesChange = (items) => {
    const sameSelected = 
      this.state.selectedInvites.length === 1 &&
      items.length === 1 &&
      this.state.selectedInvites[0][this.customInviteUniqProperty] === items[0][this.customInviteUniqProperty];

    if (sameSelected) {
      return;
    }

    this.setState(
      (state) => ({
        ...state,
        selectedInvites: items,
      }), 
    );
  }

  handlePedigreeOpen = () => {
    const selectedIndividualId = this.state.selectedIndividuals[0].id;
    openPedigreeFromIndividual(selectedIndividualId);
  }

  handleDatasheetOpen = () => {
    const selectedIndividualId = this.state.selectedIndividuals[0].id;
    openDatasheetFromIndividual(selectedIndividualId);
  }

  get selectedIndividual () {
    if (this.state.selectedIndividuals.length === 1) {
      return this.state.selectedIndividuals[0];
    }

    return null;
  }

  get individualDetailsTabs() {
    return {
      [this.individualDetailsTabsTypes.invites]: {
        tabProps: {
          value: 'invites',
          label: I18n.t('Invites'),
          ...getTestAttributes({ id: 'ind-home-tab-bpane-tabs-tab0' }),
        },
        component: (
          <InvitesTable
            customInviteUniqProperty={this.customInviteUniqProperty}
            individualSelected={Boolean(this.state.selectedIndividuals.length)}
            className="Individuals__content__folder-details__individual-details__information"
            getData={(loadMetadata) => this.getInvites({
              columns: this.state.invitesTableColumns,
              invites: this.state.invites,
              selectedIndividuals: this.state.selectedIndividuals,
              ...loadMetadata,
            })}
            lockInvite={updateLockStatus}
            showErrorMessage={this.showErrorMessage}
            data={this.state.invites}
            columns={this.state.invitesTableColumns}
            totalCount={this.state.invitesTotalCount}
            getColumnId={this.getColumnId}
            filters={this.state.tableFilters}
            selectedInvites={this.state.selectedInvites}
            selectedIndividual={this.selectedIndividual}
            onSelectedInvitesChange={this.handleSelectedInvitesChange}
            loadMetadata={this.state.invitesTableLoadMetadata}
            showInfoMessage={this.showInfoMessage}
            getAdminSmartListFunctions={getAdminSmartListFunctions}
            getInviteHistory={getInviteHistory}
            setLoadMetadata={this.setInvitesLoadMetadata}
            onNewInviteOpen={this.handleNewInviteOpenFromInvites}
            onRestoreColumnsToDefault={this.handleRestoreInvitesColumnsToDefault}
            onQuestionnaireOpen={this.handleQuestionnaireOpen}
            onColumnsChange={this.handleInvitesColumnsChange}
            onResize={this.handleInvitesColumnsResize}
            onColumnAdd={this.handleInvitesColumnsChange}
            onColumnRemove={this.handleInvitesColumnsChange}
            onReminderOpen={this.handleReminderOpen}
            onDatasheetOpen={this.handleDatasheetOpen}
            onPedigreeOpen={this.handlePedigreeOpen}
          />
        ),
      }
    };
  }

  handleIndividualDetailsToggle = () => {
    this.setState((x) => ({
      ...x,
      individualDetailsOpened: !x.individualDetailsOpened,
    }));
  }

  setInvitesLoadMetadata = (metadata) => {
    this.setState((state) => ({ 
      ...state, 
      invitesTableLoadMetadata: { 
        ...state.invitesTableLoadMetadata, 
        ...metadata 
      } 
    }));
  }

  handleIndividualsColumnsResize = async (columns) => {
    try {
      const processedColumns = this.state.individualsTableColumns.map((x) => ({
        ...x,
        width: columns[this.getColumnId(x)] || x.width,
      }));
  
      this.setState((x) => ({ ...x, individualsTableColumns: processedColumns }));
      await saveIndividualsColumns(processedColumns);
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  handleInvitesColumnsResize = async (columns) => {
    try {
      const processedColumns = this.state.invitesTableColumns.map((x) => ({
        ...x,
        width: columns[this.getColumnId(x)] || x.width,
      }));
  
      this.setState((x) => ({ ...x, invitesTableColumns: processedColumns }));
      await saveInvitesColumns(processedColumns);
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  setMessageProps = (props) => {
    this.setState((state) => ({
      ...state,
      messageProps: {
        ...state.messageProps,
        confirmText: 'Ok',
        cancelText: '',
        size: 'small',
        ...props,
      },
    }));
  }

  showMessage = (config) => {
    this.setMessageProps({
      show: true,
      onCustomOk: this.closeMessage,
      ...config,
    });
  }

  closeMessage = () => {
    this.setMessageProps({ show: false });
  }

  showErrorMessage = (message, size = 'small') => {
    this.showMessage({
      header: I18n.t('Operation canceled'),
      content: <Flex style={{ overflow: 'auto' }} alignItems="center"><ErrorIcon className="Individuals__message-icon" /> {message}</Flex>,
      size
      // TODO refactor with ErrorMessage component
    });
  }

  handleSetDefaultForAllUsers = async () => {
    try {
      await setDefaultColumnsForToAllUsers(this.state.individualsTableColumns);
    } catch (err) {
      this.showErrorMessage(err?.entity);
      console.error(err);
    }
  }

  handleSetDefaultForSpecificUSers = async (profiles) => {
    return setDefaultColumnsForSpecificUsers(this.state.individualsTableColumns, profiles.map((x) => x.name));
  }

  handleApplyCurrentColumnsToAllUsers = async () => {
    try {
      await applyCurrentColumnsToAllUsers(this.state.individualsTableColumns);
    } catch (err) {
      this.showErrorMessage(err?.entity);
      console.error(err);
    }
  }

  handleApplyCurrentColumnsToSpecificUsers = async (profiles) => {
    return applyCurrentColumnsToSpecificProfiles(this.state.individualsTableColumns, profiles.map((x) => x.name));
  }

  handleRestoreIndividualsColumnsToDefault = async () => {
    try {
      const columns = await restoreIndividualsColumnsToDefault();
      const newIndividuals = [];
      const newIndividualsLoadMetadata = {
        ...this.state.individualsTableLoadMetadata,
        sorting: undefined,
        filtering: {},
        pagination: {
          start: 0,
          size: 45,
        },
      };

      await this.getIndividuals({
        columns,
        selectedFolderId: this.state.selectedFolderId,
        individuals: newIndividuals,
        probandsOnly: this.state.probandsOnly,
        search: this.state.search,
        ...newIndividualsLoadMetadata,
      });

      this.setState(
        (state) => ({
          ...state,
          selectedIndividuals: [],
          selectedInvites: [],
          invites: [],
          invitesTotalCount: 0,
        }), 
      );
    } catch (err) {
      this.showErrorMessage(err?.entity);
      console.error(err);
    }
  }

  handleRestoreInvitesColumnsToDefault = async () => {
    const newInvitesLoadMetadata = {
      sorting: {},
      filtering: {},
      pagination: {
        start: 0,
        size: 45,
      },
    };
    const newInvites = [];

    try {
      const columns = await restoreInvitesColumnsToDefault();

      this.setState(
        (state) => ({
          ...state,
          selectedInvites: [],
          invitesTotalCount: 0,
        }), 
      );

      await this.getInvites({
        columns,
        invites: newInvites,
        selectedIndividuals: this.state.selectedIndividuals, 
        ...newInvitesLoadMetadata,
      });
    } catch (err) {
      console.error(err);
      let errorMessage = I18n.t('Failed to save columns');

      throw {
        entity: errorMessage,
      }
    }
  }

  handleIndividualsColumnsChange = async (columns) => {
    const newIndividuals = [];
    const newIndividualsLoadMetadata = {
      ...this.state.individualsTableLoadMetadata,
      filtering: {},
      pagination: {
        start: 0,
        size: 45,
      },
    };

    try {
      await saveIndividualsColumns(columns.map((x) => ({ ...x, field: { ...x.field, context: CONTEXT_TYPE.individual } })));  

      this.setState(
        (state) => ({
          ...state,
          individualsTableLoadMetadata: newIndividualsLoadMetadata,
          selectedIndividuals: [],
          selectedInvites: [],
          individualsTotalCount: 0,
          invites: [],
          invitesTotalCount: 0,
        }), 
      );

      const individualsColumns = await this.getIndividualsTableColumns();
      await this.getIndividuals({
        columns: individualsColumns,
        selectedFolderId: this.state.selectedFolderId,
        individuals: newIndividuals,
        probandsOnly: this.state.probandsOnly,
        search: this.state.search,
        ...newIndividualsLoadMetadata,
      });
    } catch (err) {
      console.error(err);

      let errorMessage = I18n.t('Failed to save columns');
      const possibleErrors = err.entity?.errors;

      if (possibleErrors?.length) {
        errorMessage = possibleErrors[0];
      }

      if (typeof err.entity === 'string') {
        errorMessage = err.entity;
      }

      throw {
        entity: errorMessage,
      }
    }
  }

  handleInvitesColumnsChange = async (columns) => {
    const newInvitesLoadMetadata = {
      ...this.state.invitesTableLoadMetadata,
      filtering: {},
      pagination: {
        start: 0,
        size: 45,
      },
    };
    const newInvites = [];

    try {
      await saveInvitesColumns(columns);

      this.setState(
        (state) => ({
          ...state,
          invitesTableLoadMetadata: newInvitesLoadMetadata,
          selectedInvites: [],
          invites: newInvites,
          invitesTotalCount: 0,
        }), 
      );
    } catch (err) {
      console.error(err);

      let errorMessage = I18n.t('Failed to save columns');
      const possibleErrors = err.entity?.errors;

      if (possibleErrors?.length) {
        errorMessage = possibleErrors[0];
      }

      if (typeof err.entity === 'string') {
        errorMessage = err.entity;
      }

      throw {
        entity: errorMessage,
      }
    }

    const invitesColumns = await this.getInvitesTableColumns();
    await this.getInvites({
      columns: invitesColumns,
      invites: newInvites,
      selectedIndividuals: this.state.selectedIndividuals, 
      ...newInvitesLoadMetadata,
    });
  }

  handleRenameIndividual = async (id, name) => {

    if (await checkIndividualNameExists({ homeFolderId: id, name })) {
      throw { entity: I18n.t("Individual name already exists in this folder") };
    }

    await renameIndividual(id, name);

    const newLoadMetadata = {
      ...this.state.individualsTableLoadMetadata,
      pagination: {
        start: 0,
        size: 45,
      },
    };
    const newIndividuals = [];
    
    this.setState(
      (state) => ({
        ...state,
        individualsTableLoadMetadata: newLoadMetadata,
        selectedIndividuals: [],
        selectedInvites: [],
        individualsTotalCount: 0,
        invites: [],
        invitesTotalCount: 0,
      }), 
    );

    this.getIndividuals({
      columns: this.state.individualsTableColumns,
      selectedFolderId: this.state.selectedFolderId,
      individuals: newIndividuals,
      search: this.state.search,
      probandsOnly: this.state.probandsOnly,
      ...newLoadMetadata,
    });
  }
  
  handleNewInviteOpenFromIndividuals = (individualId) => {
    this.setState((x) => ({
      ...x,
      newInviteDialogProps: {
        ...x.newInviteDialogProps,
        individualId,
        opened: true,
      },
    }));
  }

  handleNewInviteOpenFromInvites = (inviteId) => {
    this.setState((x) => ({
      ...x,
      newInviteDialogProps: {
        ...x.newInviteDialogProps,
        inviteId,
        individualId: this.parentIndividualIdOfCurrentInvites,
        opened: true,
      },
    }));
  }

  closeNewInviteDialog = () => {
    this.setState((x) => ({
      ...x,
      newInviteDialogProps: {
        ...x.newInviteDialogProps,
        inviteId: null,
        individualId: null,
        opened: false,
      },
    }));
  }

  handleQuestionnaireOpen = (data) => {
    this.setState((x) => ({
      ...x,
      openQuestionnaireDialogProps: {
        ...x.openQuestionnaireDialogProps,
        id: data.globalId ?? this.parentIndividualIdOfCurrentInvites,
        itemName: data.name,
        opened: true,
      },
    }));
  }

  closeQuestionnaireDialog = () => {
    this.setState((x) => ({
      ...x,
      openQuestionnaireDialogProps: {
        ...x.openQuestionnaireDialogProps,
        id: null,
        itemName: '',
        opened: false,
      },
    }));
  }

  handleReminderOpen = (data) => {
    let fhq4 = false;
    try {
      fhq4 = JSON.parse(data.fhq4);
    } catch {}

    this.setState((x) => ({
      ...x,
      sendReminderDialogProps: {
        ...x.sendReminderDialogProps,
        inviteId: data.id,
        fhq4,
        individualId: this.parentIndividualIdOfCurrentInvites,
        opened: true,
      },
    }));
  }

  closeReminderDialog = () => {
    this.setState((x) => ({
      ...x,
      sendReminderDialogProps: {
        ...x.sendReminderDialogProps,
        inviteId: null,
        individualId: null,
        opened: false,
      },
    }));
  }

  showInfoMessage = (message) => {
    this.showMessage({
      header: I18n.t('Information Message'),
      content: <Flex><InfoIcon className="Individuals__message-icon" />  {message}</Flex>,
      confirmText: 'Ok',
      onCustomOk: this.closeMessage,
    });
  }

  handleNewInviteSubmit = async (data) => {
    await sendInvite(data);
    this.closeNewInviteDialog();
    this.showInfoMessage(I18n.t('The Questionnaire invitation emails were successfully submitted'));
  }

  handleNewReminderSubmit = async (data) => {
    await sendInvite(data);
    this.closeReminderDialog();
    this.showInfoMessage(I18n.t('The Questionnaire reminder email was successfully sent'));
  }

  handleQuestionnaireSubmit = async (selectedQuestionnaire) => {
    const currentUrl = getCurrentUrl();
    const { url } = await openInviteQuestionnaire(selectedQuestionnaire, currentUrl);

    window.location.href = url;
  }

  toggleNewIndividualDialog = (isOpened) => {
    this.setState((x) => ({
      ...x,
      newIndividualDialogProps: {
        ...x.newIndividualDialogProps,
        opened: isOpened,
      },
    }));
  }

  toggleFolderManagementDialog = ({ opened, folder, mode }) => {
    this.setState((x) => ({
      ...x,
      folderManagementDialogProps: {
        ...x.folderManagementDialogProps,
        opened,
        folder,
        mode
      },
    }));
  }

  toggleMoveFolderDialog = ({ opened, folder }) => {
    this.setState((x) => ({
      ...x,
      moveFolderDialogProps: {
        ...x.moveFolderDialogProps,
        opened,
        folder
      },
    }));
  }

  openMoveFolderDialog = (folderId, folder) => {
    this.toggleMoveFolderDialog({ opened: true, folder });
  }

  closeMoveFolderDialog = () => {
    this.toggleMoveFolderDialog({ opened: false });
  }

  closeFolderManagementDialog = () => {
    this.toggleFolderManagementDialog({ opened: false });
  }

  openNewFolderOpen = (folderId, folder) => {
    this.toggleFolderManagementDialog({ opened: true, folder, mode: FolderManagementActionMode.create });
  }

  openEditFolderDialog = (folderId, folder) => {
    this.toggleFolderManagementDialog({ opened: true, folder, mode: FolderManagementActionMode.edit });
  }

  closeNewIndividualDialog = () => {
    this.toggleNewIndividualDialog(false);
  }

  openNewIndividualDialog = () => {
    this.toggleNewIndividualDialog(true);
  }

  handleNewIndividual = (globalId) => {
    this.closeNewIndividualDialog();
    openDatasheetFromIndividual(globalId);
  }

  saveIndividualFolder = async (folderDto) => {
    await savePedigreeFolder(folderDto);
    this.closeFolderManagementDialog();
    this.getFolders();
  }

  handleRenameFolderClick = async (id, name) => {
    try {
      await renamePedigreeFolder({ id, name });
      this.getFolders();
    } catch (err) {
      console.error(err);

      const errorMessage = err?.entity || I18n.t('Failed to rename folder');
      this.showErrorMessage(errorMessage);
    }
  }

  handleDeleteFolderClick = async (folderId) => {
    try {
      await deletePedigreeFolder(folderId);
      this.getFolders();
    } catch (err) {
      console.error(err);

      const errorMessage = err?.entity || I18n.t('Failed to delete folder');
      this.showErrorMessage(errorMessage);
    }
  }

  handleOpenNewSpreadsheet = () => {
    openNewSpreadsheet(this.state.selectedFolderId);
  }

  handleMoveIndividualFolder = async (folderDto) => {
    await movePedigreeFolder(folderDto);
    this.closeMoveFolderDialog();
    this.getFolders();
  }

  handleHeaderFilter = (filteredFields) => {
    const state = {
      ...this.state,
      ...filteredFields,
      loadingIndividuals: true,
      individualsTableLoadMetadata: {
        ...this.state.individualsTableLoadMetadata,
        pagination: {
          start: 0,
          size: 45,
        },
      },
      selectedIndividuals: [],
      selectedInvites: [],
      individualsTotalCount: 0,
      invites: [],
      invitesTotalCount: 0,
      individuals: []
    }
    this.setState(state);

    this.getIndividuals({
      search: state.search,
      probandsOnly: state.probandsOnly,
      columns: this.state.individualsTableColumns,
      selectedFolderId: this.state.selectedFolderId,
      individuals: [],
      ...state.individualsTableLoadMetadata
    });
  }

  getFoldersActions = () => {
    const actions = [];

    if (this.state.userPermissions.canAddFolder) {
      actions.push({ label: I18n.t('New Folder'), onClick: this.openNewFolderOpen });
    }

    if (this.state.userPermissions.admin) {
      actions.push({ label: I18n.t('Edit Folder'), onClick: this.openEditFolderDialog });
    }

    if (this.state.userPermissions.canModifyFolder) {
      actions.push(
          { label: I18n.t('Move Folder'), onClick: this.openMoveFolderDialog },
          { type: TreeActionType.rename, label: I18n.t('Rename Folder') }
      );
    }

    actions.push({ label: I18n.t('Open Individual Spreadsheet for Folder'), onClick: this.handleOpenNewSpreadsheet });

    if (this.state.userPermissions.canDeleteFolder) {
      actions.push({ type: TreeActionType.delete, label: I18n.t('Delete Folder') });
    }

    return actions;
  }
 
  refreshIndividuals = async () => {
    await this.getIndividuals({
      search: this.state.search,
      columns: this.state.individualsTableColumns,
      selectedFolderId: this.state.selectedFolderId,
      probandsOnly: this.state.probandsOnly,
      individuals: [],
      filtering: this.state.individualsTableLoadMetadata.filtering,
      pagination: {
        start: 0,
        size: 45,
      },
      sorting: this.state.individualsTableLoadMetadata.sorting,
    });
    this.setState((x) => ({
      ...x,
      invites: [],
      invitesTotalCount: 0,
      selectedIndividuals: [],
      selectedInvites: [],
    }));
  }

  handleIndividualDelete = async (familyId) => {
    await deleteIndividual(familyId);
    await this.refreshIndividuals();
  }

  handleIndividualChangeGender = async (individualId, gender) => {
    await changeIndividualGender(individualId, gender);
    await this.refreshIndividuals();
  }

  getIndividualEmailForNewInvite = (questionnaire) => {
    return getIndividualEmail({
      fhq4: questionnaire.fhq4,
      individualId: this.state.newInviteDialogProps.individualId,
      questionnaireId: questionnaire.questionnaireId,
    });
  }

  getIndividualEmailForSendReminder = (questionnaire) => {
    return getIndividualEmail({
      fhq4: questionnaire?.fhq4,
      individualId: this.state.sendReminderDialogProps.individualId,
      questionnaireId: questionnaire?.questionnaireId,
    });
  }

  getFoldersHeaderText = ({ selectedFolderName, totalItemsCount }) =>
    selectedFolderName
      ? `${totalItemsCount} ${I18n.t("Individuals")} ${I18n.t("in")} ${selectedFolderName}`
      : `${totalItemsCount} ${I18n.t("Individuals")}`

  render() {
    const {
      folders,
      selectedFolderId,
      individuals,
      individualsTotalCount,
      selectedIndividuals,
      selectedIndividualDetailsTab,
      individualDetailsOpened,
      individualsTableColumns,
      individualsTableLoadMetadata,
      messageProps,
      tableFilters,
      newInviteDialogProps,
      openQuestionnaireDialogProps,
      loadingIndividuals,
      sendReminderDialogProps,
      newIndividualDialogProps,
      folderManagementDialogProps,
      moveFolderDialogProps,
      probandsOnly,
      userPermissions,
    } = this.state;

    return (
      <div className="Individuals">
        <MessageBox
          hideMessageBox={this.closeMessage}
          {...messageProps}
        />
        {newInviteDialogProps.opened && (
          <NewInvite
            getFamilyIdMembersCount={getFamilyIdMembersCount}
            getIndividualFamilyData={getIndividualFamilyData}
            getPedigreeFolders={getPedigreeFolders}
            createPedigreeAndMoveToFolder={createPedigreeAndMoveToFolder}
            checkPedigreeNameExists={checkPedigreeNameExists}
            getQuestionnaires={getQuestionnaires}
            individualId={newInviteDialogProps.individualId}
            showErrorMessage={this.showErrorMessage}
            showInfoMessage={this.showInfoMessage}
            getIndividualEmail={this.getIndividualEmailForNewInvite}
            onClose={this.closeNewInviteDialog}
            onSubmit={this.handleNewInviteSubmit}
            saveIndividualEmail={(params) => saveIndividualEmail({ ...params, inviteId: newInviteDialogProps.inviteId })}
          />
        )}
        {openQuestionnaireDialogProps.opened && (
          <QuestionnaireOpen 
            itemName={openQuestionnaireDialogProps.itemName}
            showErrorMessage={this.showErrorMessage}
            onClose={this.closeQuestionnaireDialog}
            onSubmit={this.handleQuestionnaireSubmit}
            getQuestionnaires={() => getSentQuestionnaires(openQuestionnaireDialogProps.id)}
          />
        )}
        {sendReminderDialogProps.opened && (
          <NewInvite
            getFamilyIdMembersCount={getFamilyIdMembersCount}
            getIndividualFamilyData={getIndividualFamilyData}
            getPedigreeFolders={getPedigreeFolders}
            createPedigreeAndMoveToFolder={createPedigreeAndMoveToFolder}
            checkPedigreeNameExists={checkPedigreeNameExists}
            getQuestionnaires={getQuestionnaires}
            getQuestionnaireForInvite={(inviteId) => getQuestionnaireForInvite(inviteId, sendReminderDialogProps.fhq4)}
            individualId={sendReminderDialogProps.individualId}
            inviteId={sendReminderDialogProps.inviteId}
            getStepsConfig={() => ({
              [NEW_INVITE_STEPS.emailTemplate]: {
                submitBtnText: I18n.t('Send a Reminder')
              }
            })}
            getSteps={() => [
              {
                step: NEW_INVITE_STEPS.pedigreeFolder,
              },
              {
                step: NEW_INVITE_STEPS.emailAddress,
              },
              {
                step: NEW_INVITE_STEPS.emailTemplate,
              },
            ]}
            saveIndividualEmail={(params) => saveIndividualEmail({ ...params, inviteId: sendReminderDialogProps.inviteId })}
            showErrorMessage={this.showErrorMessage}
            getIndividualEmail={this.getIndividualEmailForSendReminder}
            onSubmit={this.handleNewReminderSubmit}
            onClose={this.closeReminderDialog}
          />
        )}
        <Header
          probandCategory={probandsOnly}
          className="Individuals__header"
          onFilterChange={this.handleHeaderFilter}
          onNewIndividualOpen={this.openNewIndividualDialog}
        />
        {newIndividualDialogProps.opened && (
          <NewIndividual
            currentFolderId={selectedFolderId}
            showErrorMessage={this.showErrorMessage}
            onSubmit={this.handleNewIndividual}
            onClose={this.closeNewIndividualDialog}
          />
        )}
        {folderManagementDialogProps.opened && (
          <FolderManagement
            mode={folderManagementDialogProps.mode}
            folders={folders}
            folder={folderManagementDialogProps.folder}
            showErrorMessage={this.showErrorMessage}
            onClose={this.closeFolderManagementDialog}
            onSave={this.saveIndividualFolder}
            getFolderSecuritySettingsClasses={this.getFolderSecuritySettingsClasses}
            getFolder={this.getFolder}
            getFolderFormats={getFolderFormats}
            getFormatFolders={getFormatFolders}
            onCreateFormatFolder={createFormatFolder}
            onDeleteFormatFolder={deleteFormatFolder}
            onDeleteFormat={deleteFormat}
          />
        )}
        {moveFolderDialogProps.opened && (
          <MoveFolder
            folders={folders}
            folder={moveFolderDialogProps.folder}
            showErrorMessage={this.showErrorMessage}
            onSubmit={this.handleMoveIndividualFolder}
            onClose={this.closeMoveFolderDialog}
          />
        )}
        <Flex width="100%" wrap="nowrap" className="Individuals__content">
          <Folders 
            getId={{
              addFolder: 'ind-home-tab-tree-add-folder-btn',
              treeFolder: ({ parentIndex, ownIndex, data }) => parentIndex === undefined 
                ? `ind-home-tab-tree-folder-${ownIndex}` 
                : `ind-home-tab-tree-folder-child-${data.id}`,
              treeCollapseIcon: ({ parentIndex, ownIndex, data }) => parentIndex === undefined 
                ? `ind-home-tab-tree-folder-${ownIndex}-exp-icon` 
                : `ind-home-tab-tree-folder-child-${data.id}-exp-icon`,
              treeActionBtn: ({ parentIndex, ownIndex, data }) => parentIndex === undefined 
                ? `ind-home-tab-tree-folder-${ownIndex}-con-icon` 
                : `ind-home-tab-tree-folder-child-${data.id}-exp-icon`,
            }}
            className="Individuals__content__folders"
            list={folders}
            selectedFolderId={selectedFolderId}
            onChange={this.setSelectedFolderId}
            totalItemsCount={individualsTotalCount}
            onNewFolderClick={this.openNewFolderOpen}
            onEditFolderClick={this.openEditFolderDialog}
            onMoveFolderClick={this.openMoveFolderDialog}
            onRenameFolderClick={this.handleRenameFolderClick}
            onDeleteFolderClick={this.handleDeleteFolderClick}
            onOpenIndividualSpreadsheetClick={this.handleOpenNewSpreadsheet}
            getFoldersActions={this.getFoldersActions}
            userPermissions={userPermissions}
            getHeaderText={this.getFoldersHeaderText}
          />
          <div className="Individuals__content__folder-details">
          <IndividualsTable
            reference={this.individualsTableRef}
              className={cx(
                  'Individuals__content__folder-details__individuals-table',
                  {
                    'Individuals__content__folder-details__individuals-table--opened': !individualDetailsOpened
                  }
              )}
              customIndividualUniqProperty={this.customIndividualUniqProperty}
              loginToken={this.state.loginToken}
              onIndividualDelete={this.handleIndividualDelete}
              onIndividualGenderChange={this.handleIndividualChangeGender}
              loading={loadingIndividuals}
              filters={tableFilters}
              columns={individualsTableColumns}
              data={individuals}
              totalCount={individualsTotalCount}
              getAdminSmartListFunctions={getAdminSmartListFunctions}
                getData={(loadMetadata) => {
                  this.setState((x) => ({ ...x, loadingIndividuals: true }));
                  return this.getIndividuals({
                    columns: this.state.individualsTableColumns,
                    selectedFolderId: this.state.selectedFolderId,
                    individuals: this.state.individuals,
                    search: this.state.search,
                    probandsOnly: this.state.probandsOnly,
                    ...loadMetadata
                  });
                }}
              folders={folders}
              selectedIndividual={this.selectedIndividual}
              getColumnId={this.getColumnId}
              loadMetadata={individualsTableLoadMetadata}
              selectedIndividuals={selectedIndividuals}
              showInfoMessage={this.showInfoMessage}
              onSelectedIndividualsChange={this.handleSelectedIndividualChange}
              onResize={this.handleIndividualsColumnsResize}
              getProfiles={getProfiles}
              showErrorMessage={this.showErrorMessage}
              onSetDefaultForAllUSers={this.handleSetDefaultForAllUsers}
              onApplyCurrentColumnsToAllUsers={this.handleApplyCurrentColumnsToAllUsers}
              onRestoreColumnToDefault={this.handleRestoreIndividualsColumnsToDefault}
              onSetDefaultForSpecificUSers={this.handleSetDefaultForSpecificUSers}
              onApplyCurrentColumnsToSpecificUsers={this.handleApplyCurrentColumnsToSpecificUsers}
              onColumnsChange={this.handleIndividualsColumnsChange}
              showMessage={this.showMessage}
              closeMessage={this.closeMessage}
              onNewInviteOpen={this.handleNewInviteOpenFromIndividuals}
              onQuestionnaireOpen={this.handleQuestionnaireOpen}
              onRenameIndividual={this.handleRenameIndividual}
          />
            <div 
              className={cx(
                'Individuals__content__folder-details__individual-details',
                {
                  'Individuals__content__folder-details__individual-details--opened': individualDetailsOpened 
                }
              )}
            >
              <Flex alignItems="flex-end" className="Individuals__content__folder-details__individual-details__header">
                <Tabs 
                  value="invites"
                  items={Object.keys(this.individualDetailsTabs).map((x) => this.individualDetailsTabs[x].tabProps)}
                />

                <button 
                  onClick={this.handleIndividualDetailsToggle}
                  className="Individuals__content__folder-details__individual-details__toggle-btn"
                  {...getTestAttributes({ id: 'ind-home-tab-bpane-collapse-btn' })}
                >
                  <i className={cx('fa', individualDetailsOpened ? 'fa-angle-down' : 'fa-angle-up' )} />
                </button>
              </Flex>
              {individualDetailsOpened && this.individualDetailsTabs[selectedIndividualDetailsTab].component}
            </div>
          </div>
        </Flex>
      </div>
    );
  }
}
